import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useAuth as useClerkAuth, useUser } from "@clerk/clerk-react";
import api, { setApiTokenGetter } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { isLoaded, isSignedIn, getToken, signOut } = useClerkAuth();
  const { user: clerkUser } = useUser();
  const [user, setUser] = useState(null); // null = loading, false = logged out
  const [quota, setQuota] = useState(null);
  const lastSig = useRef(null);

  // Wire Clerk's session token into the axios client.
  useEffect(() => {
    setApiTokenGetter(isSignedIn ? () => getToken() : null);
  }, [isSignedIn, getToken]);

  const loadUser = useCallback(async () => {
    if (!isLoaded) {
      setUser(null);
      return;
    }
    if (!isSignedIn) {
      setUser(false);
      return;
    }
    // Retry a few times: right after sign-in the session token can lag a beat.
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        return;
      } catch {
        await new Promise((r) => setTimeout(r, 500));
      }
    }
    setUser(false);
  }, [isLoaded, isSignedIn]);

  const refreshQuota = useCallback(async () => {
    try {
      const { data } = await api.get("/quota/me");
      setQuota(data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    if (user && user !== false) refreshQuota();
  }, [user, refreshQuota]);

  // When the user edits their Clerk profile (name/avatar) via the UserButton,
  // mirror the change into our backend and refresh the in-app UI.
  useEffect(() => {
    if (!isSignedIn || !clerkUser) return;
    const sig = `${clerkUser.fullName || ""}|${clerkUser.imageUrl || ""}|${clerkUser.primaryEmailAddress?.emailAddress || ""}`;
    if (lastSig.current === null) {
      lastSig.current = sig; // skip the initial render (loadUser already handled it)
      return;
    }
    if (lastSig.current !== sig) {
      lastSig.current = sig;
      api
        .post("/auth/sync")
        .then(({ data }) => setUser(data))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clerkUser?.fullName, clerkUser?.imageUrl, clerkUser?.primaryEmailAddress?.emailAddress, isSignedIn]);

  const logout = async () => {
    try {
      await signOut();
    } catch {
      /* ignore */
    }
    setUser(false);
    setQuota(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, quota, refreshQuota, logout, loadUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
